
import { useState, useEffect } from 'react';
import { Vendor } from '@/types';
import { fetchProductFeedProcessList } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertCircle, Check, X, Download, RefreshCw } from 'lucide-react';
import VendorSelect from '@/components/VendorSelect';

interface ProductFeedHistoryProps {
  selectedVendor: Vendor | null;
  onVendorSelect: (vendor: Vendor | null) => void;
}

interface TaskStatus {
  bucket?: string;
  key?: string;
  collectionName?: string;
  status: string;
  task: string;
  updated_at: string;
  rawProductFeedValidationReportLocation?: string;
}

interface ProductFeedRecord {
  _id: string;
  id: number;
  opType: string;
  vendorName: string;
  rawProductFeedLocation: string;
  rawProductFeedArchiveLocation: string;
  rawProductFeedValidationReportLocation: string;
  status: string;
  collectionName: string;
  created_at: string;
  updated_at?: string;
  taskStatus?: TaskStatus[];
}

interface ProductFeedHistoryResponse {
  data: ProductFeedRecord[];
}

const ProductFeedHistory = ({ selectedVendor, onVendorSelect }: ProductFeedHistoryProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [history, setHistory] = useState<ProductFeedRecord[]>([]);
  const { toast } = useToast();

  const fetchHistory = async () => {
    setLoading(true);
    
    console.log('Fetching product feed processing history');
    if (selectedVendor) {
      console.log(`Fetching product feed history for specific vendor: ${selectedVendor.vendorName}`);
    } else {
      console.log('Fetching product feed history for all vendors');
    }

    try {
      const responseData: ProductFeedHistoryResponse = await fetchProductFeedProcessList(
        selectedVendor ? selectedVendor.vendorName : undefined
      );
      
      // Check if data property exists and is an array
      if (responseData && Array.isArray(responseData.data)) {
        console.log(`Retrieved ${responseData.data.length} product feed processing records`);
        setHistory(responseData.data);
      } else {
        console.warn('Product feed history data is missing or not an array:', responseData);
        setHistory([]);
      }
    } catch (error) {
      console.error('Error fetching product feed processing history:', error);
      toast({
        title: "Error",
        description: `Failed to load product feed processing history: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive"
      });
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('ProductFeedHistory component - useEffect triggered');
    fetchHistory();
  }, [selectedVendor]);

  // Function to get task status
  const getTaskStatus = (record: ProductFeedRecord, taskName: string): TaskStatus | undefined => {
    return record.taskStatus?.find(task => task.task === taskName);
  };

  // Function to render status badge
  const renderStatus = (status: string) => {
    const statusColors = {
      'OPEN': 'bg-blue-100 text-blue-800',
      'INPROGRESS': 'bg-yellow-100 text-yellow-800',
      'COMPLETED': 'bg-green-100 text-green-800',
      'FAILED': 'bg-red-100 text-red-800'
    };
    
    return (
      <Badge className={statusColors[status as keyof typeof statusColors] || 'bg-gray-100 text-gray-800'}>
        {status}
      </Badge>
    );
  };

  // Function to render task status
  const renderTaskStatus = (status: string) => {
    return status === 'completed' ? 
      <span className="text-green-600 font-bold"><Check size={16} /></span> : 
      status === 'failed' ? 
      <span className="text-red-600 font-bold"><X size={16} /></span> :
      <span className="text-yellow-600 font-bold"><AlertCircle size={16} /></span>;
  };

  // Function to download file
  const downloadFile = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast({
        title: "Success",
        description: `Downloaded ${filename}`,
      });
    } catch (error) {
      console.error('Download failed:', error);
      toast({
        title: "Error",
        description: `Failed to download ${filename}: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive"
      });
    }
  };

  // Function to format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Function to extract filename from path
  const extractFilename = (path: string) => {
    return path.split('/').pop() || path;
  };

  return (
    <div className="space-y-4 w-full">
      <Card className="w-full max-w-none">
        <CardHeader>
          <CardTitle>Product Feed Processing History</CardTitle>
          <CardDescription>
            {selectedVendor 
              ? `View product feed processing history for ${selectedVendor.vendorDisplayName}`
              : 'View product feed processing history for all vendors'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <VendorSelect onVendorSelect={onVendorSelect} disabled={loading} />
            <Button
              variant="outline"
              size="sm"
              onClick={fetchHistory}
              disabled={loading}
              className="flex items-center gap-2"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Refresh
            </Button>
          </div>
          
          {loading ? (
            <div className="py-6 text-center text-gray-500">Loading history data...</div>
          ) : history.length === 0 ? (
            <div className="py-6 text-center text-gray-500">
              {selectedVendor 
                ? `No product feed processing records found for ${selectedVendor.vendorDisplayName}` 
                : "No product feed processing records found"}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Raw Feed Upload</TableHead>
                    <TableHead>Raw Feed Validate</TableHead>
                    <TableHead>Updated Sheet</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((record) => {
                    const uploadTask = getTaskStatus(record, 'RawFeedUpload');
                    const validateTask = getTaskStatus(record, 'RawFeedValidate');
                    
                    return (
                      <TableRow key={record._id}>
                        <TableCell className="font-mono text-sm">{record.id}</TableCell>
                        <TableCell className="capitalize">{record.vendorName}</TableCell>
                        <TableCell>{renderStatus(record.status)}</TableCell>
                        <TableCell className="text-center">
                          {uploadTask ? renderTaskStatus(uploadTask.status) : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          {validateTask ? renderTaskStatus(validateTask.status) : '-'}
                        </TableCell>
                        <TableCell>
                          {validateTask?.rawProductFeedValidationReportLocation ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => downloadFile(
                                validateTask.rawProductFeedValidationReportLocation!,
                                extractFilename(validateTask.rawProductFeedValidationReportLocation!)
                              )}
                              className="flex items-center gap-1"
                            >
                              <Download size={14} />
                              Download
                            </Button>
                          ) : (
                            <span className="text-gray-400 text-sm">Not available</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductFeedHistory;